import type { ReactNode } from "react"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import getBaseMarkdownOverrides from "@gatsby-plugin-generic-page/helpers/getBaseMarkdownOverrides"
import type {
  ProductBoxButtonType,
  ProductBoxParameters,
} from "@gatsby-plugin-generic-page/types/productBoxTypes"
import { Divider, Text, Tooltip } from "@ionos-web-design-system/react"
import get from "lodash/get"
import DynamicUdsIcon from "~/components/DynamicUdsIcon"
import type { ProductBoxBundle } from "~/fragments/bundle"
import type { ProductBoxSlimContent as ProductBoxSlimContentProps } from "~/hooks/useProductBoxSlim"
import isEmpty from "~/lib/isEmpty"

export default function productBoxContent(
  boxParameters: ProductBoxParameters[],
  type: ProductBoxButtonType,
  bundle?: ProductBoxBundle[],
  isTrialBox?: boolean,
  icon?: ReactNode
): ProductBoxSlimContentProps {
  const contentParameters: ReactNode[] = []

  if (!isEmpty(boxParameters)) {
    boxParameters.forEach((boxParameter) => {
      const { groupTitle, parameters } = boxParameter.flatData

      const parameterItems = parameters.map((parameter) => {
        const tooltipContent =
          !parameter.isInactive && parameter.tooltipText ? (
            <MarkdownText
              components={{
                ...getBaseMarkdownOverrides({
                  typography: "small1",
                  boldTypography: "small4",
                  color: "base",
                }),
              }}
            >
              {parameter.tooltipText}
            </MarkdownText>
          ) : undefined

        const labelText = parameter.isBoldLabel
          ? `**${parameter.label}**`
          : parameter.label

        const valueText =
          parameter.value &&
          (parameter.isBoldValue ? `**${parameter.value}**` : parameter.value)

        const parameterContent = (
          <Text
            variant="bodySm"
            color={parameter.isInactive ? "muted" : "base"}
            className={
              parameter.isInactive
                ? "line-through"
                : !isEmpty(tooltipContent)
                  ? "cursor-help underline decoration-dashed"
                  : undefined
            }
          >
            {[labelText, valueText].filter(Boolean).join(" ")}
          </Text>
        )

        return (
          <div
            key={parameter.label}
            className="flex items-center mb-3 last:mb-0 gap-gap-14"
          >
            {isTrialBox && icon ? (
              <span className="shrink-0">{icon}</span>
            ) : null}

            {parameter.icon ? (
              <DynamicUdsIcon
                iconName={parameter.icon.iconId}
                size="small"
                className="shrink-0"
              />
            ) : null}

            {!isEmpty(tooltipContent) ? (
              <Tooltip content={tooltipContent}>{parameterContent}</Tooltip>
            ) : (
              parameterContent
            )}
          </div>
        )
      })

      contentParameters.push(
        <div key={boxParameter.id} className="flex flex-col">
          {!isTrialBox ? <Divider className="my-3" /> : null}

          <div className="p-3">
            {groupTitle ? (
              <Text variant="bodySm" weight="black" className="mb-3">
                {groupTitle}
              </Text>
            ) : null}

            <div className="flex flex-col">{parameterItems}</div>
          </div>
        </div>
      )
    })
  }

  const tooltipText: string | null = !isEmpty(bundle)
    ? get(bundle[0], "flatData.tooltip[0].flatData.text", null)
    : null

  const content: ProductBoxSlimContentProps = {
    parameters: contentParameters,
    footer: bundle?.length
      ? {
          isBundle: type === "plan" && !isEmpty(bundle[0].flatData.plan),
          label: bundle[0].flatData.text,
          value: bundle[0].flatData.value || undefined,
          tooltip: !isEmpty(tooltipText) ? (
            <div>
              <MarkdownText
                components={{
                  ...getBaseMarkdownOverrides({
                    typography: "small1",
                    boldTypography: "small4",
                    color: "base",
                  }),
                  br: () => null,
                }}
              >
                {tooltipText}
              </MarkdownText>
            </div>
          ) : undefined,
        }
      : undefined,
  }

  return content
}
